import { DocumentContext } from '@harvey-frontend/openapi/models/DocumentContext'
import { DraftingApplyEditRequest } from '@harvey-frontend/openapi/models/DraftingApplyEditRequest'
import { DraftingEditSuggestion } from '@harvey-frontend/openapi/models/DraftingEditSuggestion'
import { DraftingEditSuggestionsRequest } from '@harvey-frontend/openapi/models/DraftingEditSuggestionsRequest'

import { DraftingEditType } from 'openapi/models/DraftingEditType'

import {
  MatchedReference,
  Playbook,
  PlaybookSchema,
} from '../../../schemas/playbook'
import {
  fetchDraftingApply,
  fetchDraftingSuggestStream,
} from '../../../services/endpoints'
import { SuggestionType } from '../hooks/usePlaybookRuleSuggestions'
import { getTextAroundCursor } from './formatting-utils'

/**
 * Runs playbook schema validation given a JSON string.
 * @param json - JSON string to validate
 * @returns Playbook object if validation is successful, otherwise an empty array.
 */
export const validatePlaybookJSON = (json: string) => {
  try {
    const parsedData = PlaybookSchema.safeParse(JSON.parse(json))
    if (!parsedData.success) {
      console.error('Error validating playbook JSON', parsedData.error)
      return {} as Playbook
    }
    return parsedData.data
  } catch (error) {
    console.error('Error parsing playbook JSON.', error)
    return {} as Playbook
  }
}

/**
 * Returns a comma-separated string of reference pages from a list of references.
 * @param references - List of playbook references
 * @returns String of deduped reference pages
 * Example: [{pageNo: 1}, {pageNo: 2}, {pageNo: 1}] => 'p. 1, 2'
 */
export const dedupeReferencePages = (references?: MatchedReference[]) => {
  if (!references) {
    return ''
  }
  const uniquePages = [
    ...references.reduce((acc, ref) => {
      acc.add(ref.pageNo)
      return acc
    }, new Set<number>()),
  ]
    .map((pageNo) => `p. ${pageNo}`)
    .join(', ')
  if (uniquePages.length > 8) {
    return uniquePages.slice(0, 8) + '…'
  }
  return uniquePages
}

export interface PlaybookEditSuggestion {
  suggestion: DraftingEditSuggestion
  clauseText: string
  ruleText: string
  type: SuggestionType
}

const formatPlaybookRuleInstruction = (ruleText: string) => {
  return `
  Please revise the text to align with the following rule:
  ${ruleText}
  `
}

export const fetchPlaybookRuleSuggestions = async (
  documentId: string,
  eventId: number,
  textToEdit: string,
  ruleText: string,
  accessToken: string,
  type: SuggestionType,
  abortController: AbortController
): Promise<PlaybookEditSuggestion[]> => {
  const documentContext = await getTextAroundCursor()
  const request: DraftingEditSuggestionsRequest = {
    instructions: formatPlaybookRuleInstruction(ruleText),
    wordDocumentId: documentId,
    eventId,
    textToEdit,
    documentContext,
  }
  const changeListResponse: DraftingEditSuggestion[] =
    await fetchDraftingSuggestStream({
      accessToken,
      abortController,
      request,
    })
  return changeListResponse.map((suggestion) => ({
    suggestion,
    clauseText: textToEdit,
    ruleText: ruleText,
    type,
  }))
}

export const fetchPlaybookRuleEdits = async (
  suggestionId: string,
  suggestionText: string,
  clauseText: string,
  documentContext: DocumentContext
) => {
  const request: DraftingApplyEditRequest = {
    id: suggestionId,
    instructions: suggestionText,
    textToEdit: clauseText,
    documentContext,
    editType: DraftingEditType.PLAYBOOK,
  }
  return await fetchDraftingApply(request)
}
